"use client";

import { Box, Grid, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import PageContainer from "@/app/components/ui-components/PageContainer";
import NormalRequestSkeleton from "./NormalRequestSkeleton";
import InstantBookingSkeleton from "./InstantBookingSkeleton";

const SkeletonCard = styled(Box)(({ theme }) => ({
  borderRadius: "10px",
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[9],
}));

const ProfileSkeleton = ({ panelType = "request" }) => {
  return (
    <Grid
      container
      spacing={3}
      sx={{
        maxWidth: "1200px",
        width: "100%",
        mx: "auto",
        px: { xs: 2, sm: 3 },
        display: { xs: "block", lg: "grid" },
        gridTemplateColumns: { lg: "7fr 5fr" },
        columnGap: { lg: 7 },
      }}
    >
      {/* Left Side - Accurate Profile Skeleton */}
      <Grid size={{ xs: 12, lg: 12 }}>
        <Box
          sx={{
            borderRadius: "10px!important",
            pt: 5,
            pb: 5,
            position: "relative",
          }}
        >
          {/* Edit button skeleton */}
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
            }}
          >
            <Skeleton variant="circular" width={40} height={40} />
          </Box>

          {/* Header with Avatar and Basic Info */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 5 }}>
            {/* Avatar */}
            <Skeleton
              variant="circular"
              width={120}
              height={120}
              sx={{ mr: 3, flexShrink: 0 }}
            />

            <Box sx={{ flex: 1 }}>
              {/* Name and verification icon */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
                <Skeleton
                  variant="text"
                  width={200}
                  height={40}
                  sx={{ mr: 1 }}
                />
                <Skeleton variant="circular" width={24} height={24} />
              </Box>

              {/* Company/Role */}
              <Box sx={{ mb: 1 }}>
                <Skeleton variant="text" width={180} height={28} />
              </Box>

              {/* Location with flag */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={24}
                  height={16}
                  sx={{ borderRadius: 0 }}
                />
                <Skeleton variant="text" width={120} height={20} />
              </Box>
            </Box>
          </Box>

          {/* Intro section */}
          <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>

          {/* About section */}
          <Box sx={{ mb: 2 }}>
            {/* About header with LinkedIn */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.75,
              }}
            >
              <Skeleton variant="text" width={80} height={24} />
              <Skeleton variant="circular" width={30} height={30} />
            </Box>

            {/* Divider */}
            <Skeleton
              variant="rectangular"
              width="100%"
              height={1}
              sx={{ mb: 4, mt: 2 }}
            />

            {/* About content */}
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
          </Box>

          {/* Expertise chips - Only 3 chips */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Skeleton
                variant="rounded"
                width={120}
                height={32}
                sx={{ borderRadius: "10px" }}
              />
              <Skeleton
                variant="rounded"
                width={100}
                height={32}
                sx={{ borderRadius: "10px" }}
              />
              <Skeleton
                variant="rounded"
                width={140}
                height={32}
                sx={{ borderRadius: "10px" }}
              />
            </Box>
          </Box>

          {/* Activity & Availability section - New narrative format */}
          <Box sx={{ mb: 4 }}>
            {/* Section title */}
            <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />

            {/* Divider */}
            <Skeleton
              variant="rectangular"
              width="100%"
              height={1}
              sx={{ mb: 4, mt: 2 }}
            />

            {/* Narrative paragraph - 3 lines */}
            <Box sx={{ mb: 3 }}>
              <Skeleton
                variant="text"
                width="100%"
                height={22}
                sx={{ mb: 0.5 }}
              />
              <Skeleton
                variant="text"
                width="95%"
                height={22}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="85%" height={22} />
            </Box>

            {/* Under profile text - 2 lines */}
            <Box sx={{ mt: 2 }}>
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          </Box>

          {/* Reviews section placeholder */}
          <Box
            sx={{
              mt: 10,
              display: { xs: "none", sm: "block" },
            }}
          >
            <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                sx={{ borderRadius: "8px" }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                sx={{ borderRadius: "8px" }}
              />
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Right Side - Dynamic Panel Skeleton */}
      {/* <Grid size={{ xs: 12, lg: 5 }}>
          <SkeletonCard
            sx={{
              height: "fit-content",
              borderRadius: "7px!important",
              paddingTop: 6,
              paddingBottom: 2,
              position: "relative",
            }}
          >
            {panelType === "instant" ? (
              <InstantBookingSkeleton />
            ) : (
              <NormalRequestSkeleton />
            )}
          </SkeletonCard>
        </Grid> */}
    </Grid>
  );
};

export default ProfileSkeleton;
